import React, { createContext, useContext, useCallback } from "react";
import { IdentityManager } from "../helpers/IdentityManager";

const EVENTS_ENDPOINT = `${process.env.GATSBY_APP_EVENTS_API}public/front/event/v2`;

interface TrackingPayload {
  visitor_id: string | null;
  session_id: string | null;
  user_id: string | null;
  event_name: string;
  url: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

interface TrackingContextValue {
  track: (eventType: string, payload?: Record<string, unknown>, isExit?: boolean) => void;
}

const TrackingContext = createContext<TrackingContextValue>({
  track: () => {},
});

interface TrackingProviderProps {
  children: React.ReactNode;
}

export const TrackingProvider: React.FC<TrackingProviderProps> = ({ children }) => {
  const track = useCallback(
    (eventType: string, payload: Record<string, unknown> = {}, isExit = false) => {
      if (typeof window === "undefined") return;

      const identity = IdentityManager.getIdentity();

      const body: TrackingPayload = {
        visitor_id: identity.visitor_id,
        session_id: identity.session_id,
        user_id: identity.user_id,
        event_name: eventType,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        payload,
      };

      const json = JSON.stringify(body);

      if (isExit && typeof navigator.sendBeacon === "function") {
        const blob = new Blob([json], { type: "application/json" });
        navigator.sendBeacon(EVENTS_ENDPOINT, blob);
        return;
      }

      fetch(EVENTS_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: json,
        keepalive: true,
      }).catch(() => {
        // Fire-and-forget: silently ignore network errors
      });
    },
    []
  );

  return (
    <TrackingContext.Provider value={{ track }}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracker = (): TrackingContextValue => {
  const context = useContext(TrackingContext);
  if (!context) {
    throw new Error("useTracker must be used within a TrackingProvider");
  }
  return context;
};
